import { Button, Label, Switch } from '@ezhire/ui';
import { useHumansOnlyStore } from '../stores/humans-only-store';
import { useAiAgentProfiles, type AiAgentProfilesOptions } from '../hooks/use-ai-agent-profiles';

export { filterHumans } from '../ai-agent-utils';

/** "Humans only" switch for per-agent reports. The choice is remembered per browser. */
export function HumansOnlyToggle({
  className,
  orgId,
  options,
}: {
  className?: string;
  orgId: string | null;
  options?: AiAgentProfilesOptions;
}) {
  const humansOnly = useHumansOnlyStore((s) => s.humansOnly);
  const setHumansOnly = useHumansOnlyStore((s) => s.setHumansOnly);
  const profiles = useAiAgentProfiles(orgId, options);
  return (
    <HumansOnlyToggleView
      className={className}
      humansOnly={humansOnly}
      setHumansOnly={setHumansOnly}
      profiles={profiles}
    />
  );
}

/** Share the control while allowing the host to supply its existing classification hook. */
export function HumansOnlyToggleView({
  className,
  humansOnly,
  setHumansOnly,
  profiles,
}: {
  className?: string;
  humansOnly: boolean;
  setHumansOnly: (value: boolean) => void;
  profiles: Pick<
    ReturnType<typeof useAiAgentProfiles>,
    'ready' | 'isPending' | 'isSuccess' | 'data' | 'isError' | 'isFetching' | 'refetch'
  >;
}) {
  return (
    <div className={`flex items-center gap-2 ${className ?? ''}`}>
      <Switch
        id="humans-only-toggle"
        checked={humansOnly}
        onCheckedChange={setHumansOnly}
        aria-label="Humans only"
      />
      <Label
        htmlFor="humans-only-toggle"
        className="cursor-pointer text-caption text-text-secondary"
      >
        Humans only
      </Label>
      {humansOnly && !profiles.ready ? (
        <span role="status" className="text-caption text-text-secondary">
          Agent identities are unavailable until your organization and connection are ready. Showing
          all agents.
        </span>
      ) : null}
      {humansOnly && profiles.ready && profiles.isPending ? (
        <span role="status" className="text-caption text-text-secondary">
          Loading agent identities… Showing all agents.
        </span>
      ) : null}
      {humansOnly && profiles.ready && profiles.isSuccess && profiles.data === null ? (
        <span role="status" className="text-caption text-text-secondary">
          Agent identities are unavailable. Showing all agents.
        </span>
      ) : null}
      {humansOnly && profiles.ready && profiles.isError ? (
        <span role="alert" className="text-caption text-destructive">
          Could not identify AI agents. Showing all agents.{' '}
          <Button
            size="sm"
            variant="secondary"
            disabled={profiles.isFetching}
            onClick={() => void profiles.refetch()}
          >
            Retry identities
          </Button>
        </span>
      ) : null}
    </div>
  );
}
